import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Heading,
  useToast,
  ButtonGroup,
  VStack,
  SimpleGrid,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import { MaskedInput } from '../../../../../../../../../../../../../components/Form/MaskedInput';
import { translateError } from '../../../../../../../../../../../../../utils/errors';
import { createUserCategoriesService } from '../../../../../../../../../../../../../services/UserCategories/CreateUserCategoriesService';
import { useVenture } from '../../../../../../../../../../../../../hooks/venture';
import { updateUserCategoriesService } from '../../../../../../../../../../../../../services/UserCategories/UpdateUserCategoriesService';
import { NumberInput } from '../../../../../../../../../../../../../components/Form/NumberInput';
import { IDetailedUserCategory } from '../../../../../../../../../../../../../services/Ventures/ShowVenturesService';
import { useAuth } from '../../../../../../../../../../../../../hooks/auth';
import { unmaskNumber } from '../../../../../../../../../../../../../utils/formatters/handleMask';

interface IHandleUserCategoryModalFormData {
  orgIndexCode: string | null;
  maxDayBookings?: number;
  maxGuestDayBookings?: number;
  maxFreeGuestDayBookings?: number;
  title: string;
}

interface IHandleUserCategoryModalProps {
  category?: IDetailedUserCategory;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const userCategoryFormSchema = Yup.object().shape({
  orgIndexCode: Yup.string()
    .nullable()
    .default(null)
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  maxDayBookings: Yup.number().when('maxGuestDayBookings', {
    is: (val?: number) => !!val,
    then: Yup.number()
      .nullable()
      .integer('Valores inteiros')
      .min(
        Yup.ref('maxGuestDayBookings'),
        'Mínimo igual ao máx. reservas de convidados',
      )
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
    otherwise: Yup.number()
      .nullable()
      .integer('Valores inteiros')
      .positive('Valores maiores que zero')
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
  }),
  guestBookingPrice: Yup.number()
    .integer('Valores inteiros')
    .positive('Valores maiores que zero')
    .nullable()
    .transform((_, originalValue) =>
      originalValue === '' ? null : unmaskNumber(originalValue),
    ),
  maxFreeGuestDayBookings: Yup.number().when('maxGuestDayBookings', {
    is: (val?: number) => typeof val === 'number',
    then: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .max(
        Yup.ref('maxGuestDayBookings'),
        'Excede o limite de reservas de convidados',
      )
      .nullable()
      .required('Requerido')
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
    otherwise: Yup.number()
      .integer('Valores inteiros')
      .min(0, 'Valores positivos')
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
  }),
  maxGuestDayBookings: Yup.number()
    .nullable()
    .integer('Valores inteiros')
    .positive('Valores maiores que zero')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  title: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required('Requerido'),
});

export const HandleUserCategoryModal = ({
  category,
  isOpen,
  onClose,
  onSave,
}: IHandleUserCategoryModalProps): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();
  const { venture, handleVenture } = useVenture();

  const { register, formState, handleSubmit, reset } = useForm({
    resolver: yupResolver(userCategoryFormSchema),
  });

  const { errors } = formState;

  useEffect(() => {
    reset({
      orgIndexCode: category?.hikcentralOrganization.orgIndexCode,
      maxDayBookings: category?.maxDayBookings,
      maxFreeGuestDayBookings: category?.maxFreeGuestDayBookings,
      maxGuestDayBookings: category?.maxGuestDayBookings,
      title: category?.title,
    });
  }, [category, reset]);

  const handleVentureModalitySubmit: SubmitHandler<IHandleUserCategoryModalFormData> =
    useCallback(
      async ({
        maxDayBookings,
        maxGuestDayBookings,
        maxFreeGuestDayBookings,
        orgIndexCode,
        title,
      }) => {
        if (venture) {
          if (!category) {
            try {
              const newUserCategory = await createUserCategoriesService({
                orgIndexCode,
                maxDayBookings,
                maxFreeGuestDayBookings,
                maxGuestDayBookings,
                title,
                ventureId: venture.id,
              });

              const userCategories = [
                newUserCategory,
                ...venture.userCategories,
              ];

              handleVenture({
                ...venture,
                userCategories,
              });

              reset({
                maxDayBookings: null,
                maxFreeGuestDayBookings: null,
                maxGuestDayBookings: null,
                title: null,
              });

              onSave();

              toast({
                title: 'Cadastrado com sucesso',
                description: 'Categoria cadastrada corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                toast({
                  title: 'Falha ao cadastrar',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'Ocorreu um erro ao cadastrar a categoria, tente novamente.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          } else {
            try {
              const updatedCategory = await updateUserCategoriesService({
                categoryId: category.id,
                orgIndexCode,
                maxDayBookings,
                maxFreeGuestDayBookings,
                maxGuestDayBookings,
                title,
              });

              handleVenture({
                ...venture,
                userCategories: venture.userCategories.map((ventureModality) =>
                  ventureModality.id === updatedCategory.id
                    ? { ...ventureModality, ...updatedCategory }
                    : ventureModality,
                ),
              });

              reset({
                maxDayBookings: null,
                maxFreeGuestDayBookings: null,
                maxGuestDayBookings: null,
                title: null,
              });

              onSave();

              toast({
                title: 'Atualizado com sucesso',
                description: 'Categoria atualizada corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                toast({
                  title: 'Falha ao atualizar',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'Ocorreu um erro ao atualizar a categoria, tente novamente.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          }
        }
      },
      [category, handleVenture, onSave, reset, toast, venture],
    );

  const handleCloseModal = useCallback(() => {
    reset({
      maxDayBookings: null,
      maxFreeGuestDayBookings: null,
      maxGuestDayBookings: null,
      title: null,
    });

    onClose();
  }, [onClose, reset]);

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={handleCloseModal}>
      <ModalOverlay />

      <ModalContent
        as="form"
        onSubmit={handleSubmit(handleVentureModalitySubmit)}
      >
        <ModalHeader>
          <Heading size="lg" fontWeight="normal">
            Categoria de membros
          </Heading>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <VStack spacing="8">
            <MaskedInput
              label="Título"
              error={errors.title}
              {...register('title')}
            />

            <SimpleGrid
              columns={[1, 1, 3]}
              spacing="8"
              w="100%"
              alignItems="flex-end"
            >
              <NumberInput
                label="Reservas por dia"
                error={errors.maxDayBookings}
                {...register('maxDayBookings')}
              />

              <NumberInput
                label="Reservas de convidados por dia"
                error={errors.maxGuestDayBookings}
                {...register('maxGuestDayBookings')}
              />

              {authenticatedAssignment?.featureGroup.features.some(
                (feature) =>
                  feature.key === 'FEATURE_GROUP_HIKCENTRAL_WRITE_ACCESS',
              ) && (
                <MaskedInput
                  label="Grupo Hikcentral"
                  error={errors.orgIndexCode}
                  {...register('orgIndexCode')}
                />
              )}

              <NumberInput
                label="Reservas grátis de convidados por dia"
                error={errors.maxFreeGuestDayBookings}
                {...register('maxFreeGuestDayBookings')}
              />
            </SimpleGrid>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={handleCloseModal}>
              Cancelar
            </Button>

            <Button
              colorScheme="green"
              type="submit"
              isLoading={formState.isSubmitting}
            >
              Salvar
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
